// apps/frontend/src/hooks/useWallet.ts

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

export interface WalletInfo {
  balance: number;
  availableToWithdraw: number;
  currency: string;
  isFrozen: boolean;
  todayEarnings: number;
  weekSalesCount: number;
}

export interface WalletBalance extends WalletInfo {
  pendingBalance: number;
  reservedBalance: number;
  escrowBalance: number;
  totalEarnings: number;
  recentTransactions: WalletTransaction[];
}

export interface WalletTransaction {
  id: string;
  type: string;
  amount: number;
  currency?: string;
  status: string;
  description?: string;
  created_at: string;
  createdAt?: string;
}

export interface WithdrawPreview {
  estimatedArrival: string;
  requiresKYC: boolean;
  requiresReview: boolean;
  riskScore: number;
  fees: number;
  stripeFeeCents?: number;
  glowpMarkupCents?: number;
  netAmount: number;
  reserve: number;
  speed?: 'standard' | 'instant';
}

export type WithdrawSpeed = 'standard' | 'instant';

const getToken = () => localStorage.getItem('glowp_access_token');

export function useWallet() {
  const [info, setInfo] = useState<WalletInfo | null>(null);
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInfo = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoadingInfo(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/wallet/info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setInfo(data.data);
    } catch {
      // silencieux — polling leger
    } finally {
      setLoadingInfo(false);
    }
  }, []);

  const fetchBalance = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoadingBalance(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setBalance(data.data);
      else setError(data.message || 'Erreur chargement wallet');
    } catch {
      setError('Impossible de charger le wallet');
    } finally {
      setLoadingBalance(false);
    }
  }, []);

  const fetchTransactions = useCallback(async (page = 1) => {
    const token = getToken();
    if (!token) return;
    setLoadingTransactions(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/wallet/transactions?page=${page}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success)
        setTransactions(data.data?.transactions ?? data.data ?? []);
    } catch {
      // silencieux
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  // NB : `amount` est reçu en EUROS (intuitif côté composant) et converti
  // en cents juste avant l'envoi backend, qui attend des cents partout
  // (WALLET_CONFIG.withdrawalMinCents = 1000 = 10 €). Sans cette conversion,
  // un user qui saisit "50" envoyait 50 → backend pensait 0,50 € → refus
  // "min 10 €" même pour un retrait légitime.
  const previewWithdraw = useCallback(
    async (
      amountEur: number,
      speed: WithdrawSpeed = 'standard'
    ): Promise<WithdrawPreview | null> => {
      const token = getToken();
      if (!token) return null;
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/v1/wallet/withdraw/preview`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              amount: Math.round(amountEur * 100),
              speed,
            }),
          }
        );
        const data = await res.json();
        return data.success ? data.data : null;
      } catch {
        return null;
      }
    },
    []
  );

  const requestWithdraw = useCallback(
    async (
      amountEur: number,
      speed: WithdrawSpeed = 'standard'
    ): Promise<{ success: boolean; message: string }> => {
      const token = getToken();
      if (!token) return { success: false, message: 'Non authentifié' };
      const idempotencyKey = `withdraw-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/wallet/withdraw`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
          },
          body: JSON.stringify({
            amount: Math.round(amountEur * 100),
            speed,
          }),
        });
        const data = await res.json();
        if (data.success) {
          await fetchInfo();
          await fetchBalance();
        }
        return { success: data.success, message: data.message || '' };
      } catch {
        return { success: false, message: 'Erreur réseau' };
      }
    },
    [fetchInfo, fetchBalance]
  );

  // Polling leger du solde toutes les 60s
  useEffect(() => {
    fetchInfo();
    const interval = setInterval(fetchInfo, 60_000);
    return () => clearInterval(interval);
  }, [fetchInfo]);

  return {
    info,
    balance,
    transactions,
    loadingInfo,
    loadingBalance,
    loadingTransactions,
    error,
    fetchInfo,
    fetchBalance,
    fetchTransactions,
    previewWithdraw,
    requestWithdraw,
  };
}
